// contexts/NotificationContext.tsx - VERSION CORRIGÉE
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { websocketService } from '../services/transferService';
import { useAuth } from './AuthContext';

interface Notification {
  id: string;
  type: 'transfer' | 'system' | 'alert';
  title: string;
  message: string;
  reference?: string;
  status?: string;
  timestamp: string;
  read: boolean;
  data?: any;
  merchant_code?: string; // AJOUTER CETTE PROPRIÉTÉ
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  showNotificationDropdown: boolean;
  setShowNotificationDropdown: (show: boolean) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  
  // Charger les notifications depuis localStorage au démarrage
  useEffect(() => {
    if (isAuthenticated && user?.merchant_code) {
      const saved = localStorage.getItem(`notifications_${user.merchant_code}`);
      if (saved) {
        try {
          const loadedNotifications = JSON.parse(saved);
          // Filtrer pour ne garder que les notifications de ce marchand
          const filteredNotifications = loadedNotifications.filter((n: Notification) => 
            !n.merchant_code || n.merchant_code === user.merchant_code
          );
          setNotifications(filteredNotifications);
        } catch (error) {
          console.error('Error loading notifications:', error);
        }
      }
    }
  }, [isAuthenticated, user?.merchant_code]);
  
  // Sauvegarder les notifications dans localStorage
  useEffect(() => {
    if (isAuthenticated && user?.merchant_code) {
      localStorage.setItem(`notifications_${user.merchant_code}`, JSON.stringify(notifications));
    }
  }, [notifications, isAuthenticated, user?.merchant_code]);
  
  // Écouter les notifications WebSocket
  useEffect(() => {
    if (!isAuthenticated || !user?.merchant_code) return;
    
    const currentMerchantCode = user.merchant_code;
    
    const handleTransferUpdate = (data: any) => {
      if (data.type === 'transfer_status_update') {
        // CORRECTION : Vérifier que la notification est pour ce marchand
        const notificationMerchantCode = data.merchant_code || data.data?.merchant_code;
        
        // IMPORTANT : Si la notification n'a pas de merchant_code, vérifier dans le message
        if (!notificationMerchantCode) {
          // Essayer d'extraire le merchant_code du message si possible
          const messageStr = String(data.message || '');
          if (messageStr.includes('merchant') || messageStr.includes('Marchand')) {
            console.warn('⚠️ Notification sans merchant_code détectée:', data);
          }
        }
        
        // FILTRAGE : Ne traiter que les notifications pour ce marchand
        if (notificationMerchantCode && notificationMerchantCode !== currentMerchantCode) {
          console.log(`🚫 Notification context ignorée - pour ${notificationMerchantCode}, nous sommes ${currentMerchantCode}`);
          return;
        }
        
        const newNotification: Notification = {
          id: `${data.reference}_${Date.now()}`,
          type: 'transfer',
          title: `Transfert ${data.reference}`,
          message: data.message,
          reference: data.reference,
          status: data.status,
          timestamp: data.timestamp || new Date().toISOString(),
          read: false,
          data: data,
          merchant_code: currentMerchantCode // AJOUTER LE MERCHANT_CODE
        };
        
        addNotification(newNotification);
      }
    };
    
    // Écouter aussi les événements personnalisés du WebSocketNotifier
    const handleCustomNotification = (event: CustomEvent) => {
      if (event.detail) {
        const { title, message, status, reference, timestamp, merchant_code } = event.detail;
        
        // FILTRAGE : Vérifier le merchant_code
        if (merchant_code && merchant_code !== currentMerchantCode) {
          console.log(`🚫 Custom notification ignorée - pour ${merchant_code}, nous sommes ${currentMerchantCode}`);
          return;
        }
        
        const newNotification: Notification = {
          id: `${reference || 'sys'}_${Date.now()}`,
          type: status ? 'transfer' : 'system',
          title: title || 'Notification',
          message: message,
          reference: reference,
          status: status,
          timestamp: timestamp || new Date().toISOString(),
          read: false,
          data: event.detail,
          merchant_code: currentMerchantCode // AJOUTER LE MERCHANT_CODE
        };
        
        addNotification(newNotification);
      }
    };
    
    websocketService.on('transfer_status_update', handleTransferUpdate);
    window.addEventListener('transferNotification' as any, handleCustomNotification);
    
    return () => {
      websocketService.off('transfer_status_update', handleTransferUpdate);
      window.removeEventListener('transferNotification' as any, handleCustomNotification);
    };
  }, [isAuthenticated, user?.merchant_code]);
  
  const addNotification = useCallback((notification: Notification) => {
    const currentMerchantCode = user?.merchant_code;
    
    // S'assurer que la notification est pour ce marchand
    if (notification.merchant_code && notification.merchant_code !== currentMerchantCode) {
      console.log(`🚫 Notification non ajoutée - pour ${notification.merchant_code}, nous sommes ${currentMerchantCode}`);
      return;
    }
    
    // S'assurer que toutes les nouvelles notifications ont un merchant_code
    const notificationWithMerchantCode = {
      ...notification,
      merchant_code: notification.merchant_code || currentMerchantCode
    };
    
    setNotifications(prev => {
      // Éviter les doublons
      const isDuplicate = prev.some(n => 
        n.reference === notification.reference && 
        n.type === notification.type &&
        Math.abs(new Date(n.timestamp).getTime() - new Date(notification.timestamp).getTime()) < 1000
      );
      
      if (isDuplicate) return prev;
      
      // Ajouter au début de la liste
      return [notificationWithMerchantCode, ...prev.slice(0, 99)]; // Garder max 100 notifications
    });
  }, [user?.merchant_code]);
  
  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);
  
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);
  
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);
  
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);
  
  // Filtrer pour ne compter que les notifications de ce marchand
  const filteredNotifications = notifications.filter(n => 
    !n.merchant_code || n.merchant_code === user?.merchant_code
  );
  
  const unreadCount = filteredNotifications.filter(n => !n.read).length;
  
  return (
    <NotificationContext.Provider value={{
      notifications: filteredNotifications, // Retourner seulement les notifications filtrées
      unreadCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      removeNotification,
      clearAllNotifications,
      showNotificationDropdown,
      setShowNotificationDropdown
    }}>
      {children}
    </NotificationContext.Provider>
  );
};